import {useState} from 'react'
import {Card, Spinner} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import {defineMessages, useIntl} from 'react-intl'
import {EXECUTIVE_SERIES} from './executiveMessages'
import {
  formatPeriodLabel,
  getApexLocaleOptions,
} from '../homeReportsPolicies/components/chartLocale'

const M = defineMessages({
  title: {id: 'dashboard.executive.trend.title', defaultMessage: 'Monthly Enrollment Overview'},
  subtitle: {
    id: 'dashboard.executive.trend.subtitle',
    defaultMessage: 'New enrollments (bars) · cancellations · never started — by month',
  },
  yEnrollments: {id: 'dashboard.executive.trend.yEnrollments', defaultMessage: 'Enrollments'},
  yCancellations: {
    id: 'dashboard.executive.trend.yCancellations',
    defaultMessage: 'Cancellations / Never Started',
  },
  logScaleTooltip: {
    id: 'dashboard.executive.trend.logScaleTooltip',
    defaultMessage: 'Toggle logarithmic scale to compare months with very different volumes',
  },
  logScale: {id: 'dashboard.executive.trend.logScale', defaultMessage: 'Log'},
  linearScale: {id: 'dashboard.executive.trend.linearScale', defaultMessage: 'Linear'},
  noDataPeriod: {
    id: 'dashboard.policies.common.noDataPeriod',
    defaultMessage: 'No data available for the selected period',
  },
})

interface IPeriodData {
  period: string
  periodLabel: string
  policiesCount: number | null
  cancellationsCount: number | null
  neverStartedCount: number | null
}

interface IExecutiveTrendChartProps {
  periodData: IPeriodData[]
  loading: boolean
}

export const ExecutiveTrendChart = ({periodData, loading}: IExecutiveTrendChartProps) => {
  const intl = useIntl()
  const [logScale, setLogScale] = useState(false)

  // The backend ships `periodLabel` already rendered in English; rebuild it
  // client-side from the raw `period` key so the axis follows the app locale.
  const categories = periodData.map((d) => formatPeriodLabel(d.period, intl.locale))
  const policiesData = periodData.map((d) => d.policiesCount ?? 0)
  const cancellationsData = periodData.map((d) => d.cancellationsCount ?? 0)
  const neverStartedData = periodData.map((d) => d.neverStartedCount ?? 0)

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: {show: true},
      zoom: {enabled: false},
      ...getApexLocaleOptions(intl.locale),
    },
    plotOptions: {
      bar: {horizontal: false, columnWidth: '55%', borderRadius: 4},
    },
    dataLabels: {enabled: false},
    stroke: {
      show: true,
      width: [0, 3, 3],
      curve: 'smooth',
    },
    xaxis: {
      categories,
      labels: {rotate: -45, rotateAlways: false},
    },
    yaxis: [
      {
        logarithmic: logScale,
        forceNiceScale: true,
        title: {text: intl.formatMessage(M.yEnrollments)},
        labels: {formatter: (v: number) => (v ?? 0).toFixed(0)},
      },
      {
        opposite: true,
        logarithmic: logScale,
        forceNiceScale: true,
        title: {text: intl.formatMessage(M.yCancellations)},
        labels: {formatter: (v: number) => (v ?? 0).toFixed(0)},
      },
      {opposite: true, show: false, logarithmic: logScale, labels: {formatter: (v: number) => (v ?? 0).toFixed(0)}},
    ],
    fill: {opacity: [1, 0.85, 0.85]},
    tooltip: {
      shared: true,
      intersect: false,
      y: {formatter: (val: number) => (val ?? 0).toString()},
    },
    legend: {position: 'top', horizontalAlign: 'left'},
    colors: ['#3699ff', '#f64e60', '#ffa800'],
  }

  const series = [
    {name: intl.formatMessage(EXECUTIVE_SERIES.newEnrollments), type: 'bar', data: policiesData},
    {
      name: intl.formatMessage(EXECUTIVE_SERIES.cancellations),
      type: 'line',
      data: cancellationsData,
    },
    {name: intl.formatMessage(EXECUTIVE_SERIES.neverStarted), type: 'line', data: neverStartedData},
  ]

  return (
    <Card className='card-flush h-100 d-flex flex-column'>
      <Card.Header className='border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage(M.title)}</span>
          <span className='text-muted fw-semibold fs-7'>{intl.formatMessage(M.subtitle)}</span>
        </h3>
        <div className='card-toolbar'>
          <button
            className={`btn btn-sm ${logScale ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setLogScale(v => !v)}
            title={intl.formatMessage(M.logScaleTooltip)}
          >
            {intl.formatMessage(logScale ? M.logScale : M.linearScale)}
          </button>
        </div>
      </Card.Header>
      <Card.Body className='d-flex flex-column flex-grow-1' style={{minHeight: 0}}>
        {loading && (
          <div className='d-flex justify-content-center align-items-center flex-grow-1'>
            <Spinner animation='border' />
          </div>
        )}
        {!loading && periodData.length === 0 && (
          <div className='d-flex justify-content-center align-items-center flex-grow-1'>
            <p className='text-muted'>{intl.formatMessage(M.noDataPeriod)}</p>
          </div>
        )}
        {!loading && periodData.length > 0 && (
          <div className='flex-grow-1' style={{minHeight: 200}}>
            <Chart options={options} series={series} type='bar' height='100%' />
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
